import {
  GetPlanSetsDocument,
  GetPlanSetsQuery,
  SavePlanSet_Input,
  SavePlanSetFormMutation,
  usePlanSetFormQuery,
  useSavePlanSetFormMutation
} from 'generated/graphql';
import _ from 'lodash';
import {
  appFormUtil,
  gqlCacheUtil
} from 'modules/common';
import React, { useEffect } from 'react';

export type IFormState = {
  id: string;
  displayName: string;
  projectId: string;

};

const initialState: IFormState = {
  id: '',
  displayName: '',
  projectId: ''
};

export function useFormState(id: string, projectId: string) {
  const {
    formState,
    dispatch,
    genFieldProps
  } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      id: {
        label: 'id',
        type: 'text'
      },
      displayName: {
        label: 'Name of Plan',
        type: 'text'
      },
      projectId: {
        label: 'projectId',
        type: 'text'
      }
    }
  });

  useEffect(() => {
    if (projectId !== '' && formState.projectId === '') {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'projectId',
          value: projectId
        }
      });
    }
  }, [
    dispatch,
    formState.projectId,
    projectId
  ]);

  const { data, called, loading } = useQuery(id);

  useEffect(() => {
    if (data.id && data.id !== '' && formState.id === '' && called && !loading) {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          ...data
        }
      });
    }
    return () => {};
  }, [
    called,
    data,
    dispatch,
    formState,
    loading
  ]);

  return React.useMemo(
    () => {
      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}


export function useSavePlanSet() {
  const [
    fnMutate,
    tuple
  ] = useSavePlanSetFormMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = React.useCallback(
    async (input: SavePlanSet_Input) => {
      const resp = await fnMutate({
        variables: {
          input
        },
        updateQueries: {
          ...gqlCacheUtil.genUpdateQueryMap<GetPlanSetsQuery, SavePlanSetFormMutation>(GetPlanSetsDocument, (prevState, { mutationResult }) => {
            if (!prevState.planSets) {
              return prevState;
            }

            if (!mutationResult.data?.savePlanSet) {
              return prevState;
            }

            return {
              ...prevState,
              planSets: _.compact([
                ...prevState.planSets,
                mutationResult.data.savePlanSet
              ])

            };
          })
        }
      });
      const id = resp.data?.savePlanSet?.id;
      return id;
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}

export interface IData extends Omit<IFormState, 'projectId'> {}

export function useQuery(id: string) {
  const tuple = usePlanSetFormQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  return React.useMemo(
    () => {
      const data: IData = {
        id: tuple.data?.planSet?.id ?? '',
        displayName: tuple.data?.planSet?.displayName ?? ''
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}

